/**
 * The ⌘K palette, wired to this console's four kinds.
 *
 * The keyboard contract, the virtual cursor and the focus rules all live in the
 * kit's `CommandPalette`. What lives HERE is everything domain-shaped: which
 * kinds exist, how the network half and the client half are interleaved, what a
 * capped group is allowed to claim, and the one thing a palette row is never
 * allowed to do — render a figure out of a stale exposure projection.
 *
 * Two halves, deliberately unequal:
 *   · Actions and pages are matched synchronously on every keystroke, so the
 *     most predictable rows are also the fastest.
 *   · Accounts, the live book and transfers are debounced 180ms and fetched.
 *     `useDeferredValue` would not do — it re-renders less, it does not make
 *     fewer network calls, and the cost being managed here is the call.
 *
 * The replaced product had no search at all: finding a member meant paging the
 * Member list, and finding a transfer meant knowing its date first. Everything
 * below exists so one keystroke replaces both.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Chip,
  CommandPalette,
  Kbd,
  RoleChip,
  StateBlock,
  type CommandPaletteGroup,
  type CommandPaletteItem,
} from '@w7/ui'
import { useChildren, useConsoleSearch, useSession } from '@/app/queries'
import type { SearchHit, SearchKind } from '@/data/api'
import { ROLE_SHORT } from '@/data/types'
import { matchActions, navHits, topActions, type ActionContext } from './actions'
import { pushRecent, readRecents, toRecent, type RecentHit } from './recents'
import { highlightParts, orderGroups, seeAllLabel, shortcutHint, statusLine } from './search'
import './search.css'

/** How long the network half waits behind the operator's hands. */
const DEBOUNCE_MS = 180
/** The live announcement is debounced harder — per-keystroke chatter is unusable. */
const ANNOUNCE_MS = 500

/** Where a capped group's "see all" row goes. One list screen per data kind. */
const SEE_ALL_TO: Record<Exclude<SearchKind, 'action'>, (text: string) => string> = {
  account: (t) => `/downline?q=${encodeURIComponent(t)}`,
  market: (t) => `/exposure?q=${encodeURIComponent(t)}`,
  transfer: (t) => `/reports/transfers?q=${encodeURIComponent(t)}`,
}

export interface GlobalSearchProps {
  open: boolean
  onClose: () => void
  /**
   * Search as a subtree rooted here rather than at the signed-in account. Left
   * for the report-filters surface to feed once its hierarchy scope picker
   * lands; until then the palette searches everything beneath you.
   */
  scopeRootId?: string
}

interface GroupSummary {
  label: string
  count: number
}

export function GlobalSearch({ open, onClose, scopeRootId }: GlobalSearchProps) {
  const navigate = useNavigate()
  const [text, setText] = useState('')
  const [debounced, setDebounced] = useState('')
  const [announced, setAnnounced] = useState('')
  const [recents, setRecents] = useState<RecentHit[]>([])

  const { data: session } = useSession()
  const account = session?.account

  // Only while the palette is open. The provider mounts this component for the
  // whole life of the app, and an eager children fetch on every page load would
  // buy nothing but a request.
  const { data: children } = useChildren(open && account ? account.id : undefined)

  const typed = text.trim()

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(typed), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [typed])

  // A reopened palette starts empty. Restoring the previous query would present
  // a result set fetched minutes ago as though it had just been asked for, and
  // on a money screen that is the whole class of bug this console exists to end.
  useEffect(() => {
    if (open) {
      setRecents(readRecents())
      return
    }
    setText('')
    setDebounced('')
  }, [open])

  /**
   * One character searches PEOPLE only — see the note on `dataGroups`.
   *
   * Asked of the REQUEST, so the scan itself narrows rather than the render.
   * Keyed off `debounced`, not `typed`: `kinds` has to describe the same query
   * the text does, or the second character would ask for all four kinds while
   * still holding the one-kind result from the first.
   */
  const kinds = useMemo<SearchKind[] | undefined>(
    () => (debounced.length === 1 ? ['account'] : undefined),
    [debounced],
  )

  const search = useConsoleSearch(debounced, scopeRootId, kinds)

  const actionContext: ActionContext = useMemo(
    () => ({ ...(account ? { role: account.role } : {}), ...(children ? { children } : {}) }),
    [account, children],
  )

  const actions = useMemo(() => matchActions(text, actionContext), [text, actionContext])

  /**
   * Does the result on screen actually answer what is in the box?
   *
   * `placeholderData` keeps the previous result visible while the next one
   * lands, which is what stops the list flashing empty between keystrokes — so
   * `data` is routinely the answer to a two-character query while the input
   * holds six. The echoed `text` is the only thing that can tell those apart,
   * and it is used for exactly one decision: whether the palette is allowed to
   * SAY "no match for …". Showing yesterday's five rows under a spinner is
   * helpful; announcing a verdict computed from a different question is not.
   */
  const fresh = search.data
  const answersCurrent = fresh?.text.trim() === debounced

  /**
   * One character searches PEOPLE only.
   *
   * A single letter against every market name, runner and remark in the open
   * book matches nearly all of them: the group would be five arbitrary rows
   * beside an honest "showing 5 of 900", which is honest and useless.
   *
   * The request already carries `kinds: ['account']` in that case, so this is a
   * belt-and-braces filter over a response that should already be narrow — it
   * matters while `placeholderData` is still showing the four-kind result from
   * the PREVIOUS keystroke, which is exactly when the wide list would flash.
   */
  const dataGroups = useMemo(() => {
    if (!fresh) return []
    const groups = typed.length === 1 ? fresh.groups.filter((g) => g.kind === 'account') : fresh.groups
    return groups.map((g) => ({ kind: g.kind, label: g.label, hits: g.hits, total: g.total }))
  }, [fresh, typed.length])

  const exposureUnavailable = Boolean(fresh?.exposureUnavailable) && typed.length > 1

  const remember = useCallback(
    (hit: Pick<SearchHit, 'id' | 'kind' | 'title' | 'subtitle' | 'to' | 'icon'>) => {
      setRecents(pushRecent(toRecent(hit)))
    },
    [],
  )

  /** Every rendered row's destination, so ⌘⏎ can open it without re-deriving. */
  const hrefs = useRef(new Map<string, string>())

  const rowFor = useCallback(
    (hit: SearchHit, keyPrefix = ''): CommandPaletteItem => {
      const id = `${keyPrefix}${hit.id}`
      hrefs.current.set(id, hit.to)
      const parts = highlightParts(hit.title, hit.ranges)
      const trail =
        hit.meta || hit.badge || hit.role ? (
          <span className="w7-search-trail">
            {hit.meta ? <span className="w7-search-metatext">{hit.meta}</span> : null}
            {/* An account carries its tier, so it gets the tier-coloured RoleChip
                the rest of the console uses — scanning a list of usernames is
                mostly scanning for rank. Everything else carries a money badge,
                pre-formatted by the projection: the palette never formats a rupee
                figure itself and never renders one the projection has disowned. */}
            {hit.role ? (
              <RoleChip role={hit.role} label={ROLE_SHORT[hit.role]} />
            ) : hit.badge ? (
              <Chip tone={hit.badge.tone} size="sm">
                {hit.badge.label}
              </Chip>
            ) : null}
          </span>
        ) : null

      return {
        id,
        // The accessible name carries everything the row shows. A <mark> splits
        // the title's text node, and without an explicit name the row is read
        // out in fragments — "man", "chester", " — Match Odds".
        label: [hit.title, hit.subtitle, hit.meta, hit.badge?.label].filter(Boolean).join('. '),
        title: parts.map((part, index) =>
          part.mark ? (
            <mark key={index} className="w7-cmdk-mark">
              {part.text}
            </mark>
          ) : (
            <span key={index}>{part.text}</span>
          ),
        ),
        subtitle: hit.subtitle,
        icon: hit.icon,
        ...(trail ? { meta: trail } : {}),
        onSelect: () => {
          remember(hit)
          navigate(hit.to)
          onClose()
        },
      }
    },
    [navigate, onClose, remember],
  )

  /* ----------------------------------------------------------- the groups -- */

  const { groups, summary } = useMemo((): {
    groups: CommandPaletteGroup[]
    summary: GroupSummary[]
  } => {
    if (!open) return { groups: [], summary: [] }

    // Rebuilt every pass, so the link map cannot outgrow what is on screen.
    hrefs.current.clear()

    /* ---- nothing typed: recents, destinations, the top actions ---- */
    if (typed.length === 0) {
      const out: CommandPaletteGroup[] = []
      if (recents.length > 0) {
        out.push({
          id: 'recent',
          label: 'Recent',
          items: recents.map((row) =>
            rowFor({ ...row, ranges: [], score: 0 }, 'recent:'),
          ),
        })
      }
      out.push({ id: 'jump', label: 'Jump to', items: navHits().map((hit) => rowFor(hit, 'jump:')) })
      out.push({
        id: 'action',
        label: 'Actions',
        items: topActions(actionContext).map((hit) => rowFor(hit, 'top:')),
      })
      return {
        groups: out,
        summary: out.map((g) => ({ label: String(g.label), count: g.items.length })),
      }
    }

    const combined = [
      { kind: 'action' as SearchKind, label: 'Actions', hits: actions, total: actions.length },
      ...dataGroups,
    ]

    const built: CommandPaletteGroup[] = []
    const counts: GroupSummary[] = []

    for (const group of orderGroups(combined)) {
      // The live book, when the projection has fallen behind. One dim row
      // instead of five figures — the StaleProjection rule applied to a surface
      // with no room for the full banner. Selecting it opens Exposure, where the
      // banner has the space to explain itself.
      if (group.kind === 'market' && exposureUnavailable) {
        built.push({
          id: 'market',
          label: group.label,
          items: [
            {
              id: 'market:unavailable',
              title: 'Live book results are unavailable',
              subtitle: 'The exposure projection has fallen behind — open Exposure for detail.',
              icon: 'warning',
              label:
                'Live book results are unavailable — the exposure projection has fallen behind. Open Exposure for detail.',
              onSelect: () => {
                navigate('/exposure')
                onClose()
              },
            },
          ],
        })
        counts.push({ label: group.label, count: 0 })
        continue
      }

      if (group.hits.length === 0) continue

      const items = group.hits.map((hit) => rowFor(hit))
      // Actions are never capped: there are a dozen of them and no "see all
      // actions" list to send anyone to. The cap exists to keep a 41-row account
      // list honest, not to ration a fixed menu.
      const dataKind = group.kind === 'action' ? null : group.kind
      const capped = dataKind !== null && group.total > group.hits.length

      if (dataKind && capped) {
        const to = SEE_ALL_TO[dataKind](typed)
        const label = seeAllLabel(dataKind, group.total)
        const id = `seeall:${dataKind}`
        hrefs.current.set(id, to)
        items.push({
          id,
          title: label ?? 'See all',
          label: label ?? 'See all',
          icon: 'chevron-right',
          onSelect: () => {
            navigate(to)
            onClose()
          },
        })
      }

      built.push({
        id: group.kind,
        label: group.label,
        items,
        // A capped list that does not say it is capped is a lie by omission —
        // the operator concludes there are five and stops looking.
        ...(capped ? { note: `Showing ${group.hits.length} of ${group.total}` } : {}),
      })
      counts.push({ label: group.label, count: group.hits.length })
    }

    return { groups: built, summary: counts }
  }, [
    open,
    typed,
    recents,
    actions,
    actionContext,
    dataGroups,
    exposureUnavailable,
    rowFor,
    navigate,
    onClose,
  ])

  /* ------------------------------------------------------------- announce -- */

  const status = useMemo(() => statusLine(summary), [summary])

  useEffect(() => {
    const timer = setTimeout(() => setAnnounced(status), ANNOUNCE_MS)
    return () => clearTimeout(timer)
  }, [status])

  /* ---------------------------------------------------------- zero states -- */

  // Never an empty list on failure: an empty list means "nothing matches",
  // which is a different fact from "the search did not answer".
  const empty = search.isError ? (
    <StateBlock
      icon="warning"
      tone="error"
      title="Search is unavailable"
      actions={
        <Button size="sm" icon="refresh" onClick={() => void search.refetch()}>
          Try again
        </Button>
      }
    >
      {search.error instanceof Error ? search.error.message : 'The search service did not answer.'}
    </StateBlock>
  ) : !answersCurrent || search.isFetching ? (
    // "No match" is a verdict, and a verdict may only be delivered by a result
    // that answered the question currently in the box.
    <StateBlock icon="search" title="Searching…" />
  ) : (
    <StateBlock
      icon="search"
      title={`No match for "${typed}"`}
      actions={
        <Button
          size="sm"
          icon="transfers"
          onClick={() => {
            navigate(`/reports/transfers?q=${encodeURIComponent(typed)}`)
            onClose()
          }}
        >
          Search transfers for &ldquo;{typed}&rdquo;
        </Button>
      }
    >
      Searched: agents and members beneath you, the events, markets and selections with an open
      position right now, and transfers by reference or remark. A market that has already settled is
      not in the live book — open My P&amp;L to find it.
    </StateBlock>
  )

  return (
    <CommandPalette
      open={open}
      onClose={onClose}
      query={text}
      onQueryChange={setText}
      groups={groups}
      loading={search.isFetching}
      empty={empty}
      status={announced}
      label="Search agents, members, markets and transfers"
      placeholder="Search agents, members, markets, transfers…"
      // Off, so ⌘⏎ genuinely means "open that one without leaving here" — the
      // whole point of the alternate is comparing two members without retyping.
      // The plain ⏎ path closes explicitly inside each row's onSelect.
      closeOnSelect={false}
      onSelectAlternate={(item) => {
        const to = hrefs.current.get(item.id)
        if (to) window.open(to, '_blank', 'noopener,noreferrer')
      }}
      footer={
        <>
          <span>
            <Kbd>↑</Kbd> <Kbd>↓</Kbd> move
          </span>
          <span>
            <Kbd>⏎</Kbd> open
          </span>
          <span>
            <Kbd>{shortcutHint() === '⌘K' ? '⌘⏎' : 'Ctrl ⏎'}</Kbd> new tab
          </span>
          <span>
            <Kbd>tab</Kbd> groups
          </span>
          <span className="w7-search-footend">
            <Kbd>esc</Kbd> close
          </span>
        </>
      }
    />
  )
}
